/**
 * TermsModal Component
 * Displays Terms and Conditions in a scrollable modal with acceptance checkbox
 */

import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { TERMS_AND_CONDITIONS } from '../content/terms-and-conditions.js';

// Simple markdown-like renderer for headings and paragraphs
function renderContent(content) {
  const lines = content.split('\n');
  const elements = [];
  let currentParagraph = [];
  
  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      elements.push(
        <p key={elements.length} className="terms-paragraph">
          {currentParagraph.join(' ')}
        </p>
      );
      currentParagraph = [];
    }
  };
  
  for (const line of lines) {
    const trimmed = line.trim();
    
    if (!trimmed) {
      flushParagraph();
      continue;
    }
    
    // Main heading
    if (trimmed.startsWith('# ')) {
      flushParagraph();
      elements.push(
        <h1 key={elements.length} className="terms-h1">
          {trimmed.substring(2)}
        </h1>
      );
    }
    // Section heading
    else if (trimmed.startsWith('## ')) {
      flushParagraph();
      elements.push(
        <h2 key={elements.length} className="terms-h2">
          {trimmed.substring(3)}
        </h2>
      );
    }
    // Regular text
    else {
      currentParagraph.push(trimmed);
    }
  }
  
  flushParagraph();
  return elements;
}

export function TermsModal({ isOpen, onAccept, onDecline, showAcceptance = true }) {
  const [accepted, setAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef(null);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAccepted(false);
      setHasScrolledToBottom(false);
    }
  }, [isOpen]);
  
  // Check if user has scrolled to bottom
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider "bottom" as within 50px of actual bottom
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="terms-modal-overlay" onClick={onDecline}>
      <div className="terms-modal" onClick={e => e.stopPropagation()}>
        <div className="terms-modal-header">
          <h2>{TERMS_AND_CONDITIONS.title}</h2>
          <span className="terms-version">Version {TERMS_AND_CONDITIONS.version}</span>
        </div>
        
        {/* Key Summary */}
        <div className="terms-summary">
          <h3>Key Points</h3>
          <ul>
            {TERMS_AND_CONDITIONS.keySummary.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
        
        {/* Scrollable Content */}
        <div 
          className="terms-modal-content" 
          ref={contentRef}
          onScroll={handleScroll}
        >
          {renderContent(TERMS_AND_CONDITIONS.content)}
        </div>
        
        {/* Scroll Indicator */}
        {!hasScrolledToBottom && (
          <div className="terms-scroll-indicator">
            ↓ Scroll to read all terms
          </div>
        )}
        
        {/* Acceptance Section */}
        {showAcceptance && (
          <div className="terms-modal-footer">
            <label className="terms-checkbox-label">
              <input 
                type="checkbox" 
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                disabled={!hasScrolledToBottom}
              />
              <span>
                I have read, understood, and agree to be bound by these Terms and Conditions
              </span>
            </label>
            
            <div className="terms-button-group">
              <button 
                className="terms-btn terms-btn-secondary"
                onClick={onDecline}
              >
                Cancel
              </button>
              <button 
                className="terms-btn terms-btn-primary"
                onClick={() => onAccept()}
                disabled={!accepted}
              >
                Accept & Continue
              </button>
            </div>
          </div>
        )}
        
        {/* View-only mode (no acceptance) */}
        {!showAcceptance && (
          <div className="terms-modal-footer">
            <button 
              className="terms-btn terms-btn-secondary"
              onClick={onDecline}
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TermsModal;
